import { useEffect, useCallback, useRef, ReactElement } from "react";
import { pdf } from "@react-pdf/renderer";

interface UsePrintPdfShortcutOptions {
  pdfDocument?: () => ReactElement | null;
  onPrint?: () => void;
  canPrint?: () => boolean;
  enabled?: boolean;
}

/**
 * Handle Ctrl/Cmd + P (and browser print via context menu) by opening generated PDF in a new tab.
 * Note: Browser print dialogs cannot be programmatically closed.
 */
const usePrintPdfShortcut = ({
  pdfDocument,
  onPrint,
  canPrint,
  enabled = true,
}: UsePrintPdfShortcutOptions): void => {
  const isGeneratingRef = useRef(false);
  const shouldReloadOnFocusRef = useRef(false);
  const reloadScheduledRef = useRef(false);
  const lastTriggerAtRef = useRef(0);

  const canPrintRef = useRef<UsePrintPdfShortcutOptions["canPrint"]>(canPrint);
  const pdfDocumentRef = useRef<UsePrintPdfShortcutOptions["pdfDocument"]>(pdfDocument);
  const onPrintRef = useRef<UsePrintPdfShortcutOptions["onPrint"]>(onPrint);

  useEffect(() => {
    canPrintRef.current = canPrint;
    pdfDocumentRef.current = pdfDocument;
    onPrintRef.current = onPrint;
  }, [canPrint, pdfDocument, onPrint]);

  const doPrint = useCallback(async () => {
    const pdfDocFactory = pdfDocumentRef.current;
    const fallbackPrint = onPrintRef.current;

    if (pdfDocFactory) {
      if (isGeneratingRef.current) return;
      isGeneratingRef.current = true;

      try {
        const pdfElement = pdfDocFactory();
        if (!pdfElement) return;

        const blob = await pdf(pdfElement).toBlob();
        const url = URL.createObjectURL(blob);
        const newTab = window.open(url, "_blank");

        if (newTab) {
          newTab.onload = () => {
            newTab.print();
            // Cleanup URL after print is triggered.
            setTimeout(() => URL.revokeObjectURL(url), 10_000);
          };
        } else {
          // If popups are blocked, at least cleanup the URL.
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error generating PDF:", error);
      } finally {
        isGeneratingRef.current = false;
      }

      return;
    }

    if (fallbackPrint) {
      fallbackPrint();
    }
  }, []);

  const canTriggerPrint = useCallback((): boolean => {
    const checker = canPrintRef.current;
    return checker ? checker() : true;
  }, []);

  const triggerPrint = useCallback(async () => {
    const now = Date.now();
    // Debounce to avoid double-fire (e.g. beforeprint/afterprint/focus sequences).
    if (now - lastTriggerAtRef.current < 400) return;
    lastTriggerAtRef.current = now;

    if (!canTriggerPrint()) return;
    await doPrint();
  }, [canTriggerPrint, doPrint]);

  const scheduleReloadIfNeeded = useCallback(() => {
    if (!shouldReloadOnFocusRef.current) return;
    if (reloadScheduledRef.current) return;

    reloadScheduledRef.current = true;
    setTimeout(() => {
      shouldReloadOnFocusRef.current = false;
      reloadScheduledRef.current = false;
      window.location.reload();
    }, 300);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const isPrintShortcut = (event.ctrlKey || event.metaKey) && key === "p";

      if (isPrintShortcut) {
        event.preventDefault();
        triggerPrint();
      }
    };

    const handleBeforePrint = () => {
      shouldReloadOnFocusRef.current = true;
      triggerPrint();
    };

    const handleAfterPrint = () => {
      // After print dialog closes, attempt reload (some browsers require focus/visibility to change).
      if (document.hasFocus()) scheduleReloadIfNeeded();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") scheduleReloadIfNeeded();
    };

    const handleFocus = () => {
      scheduleReloadIfNeeded();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [enabled, scheduleReloadIfNeeded, triggerPrint]);
};

export default usePrintPdfShortcut;
